import type { MarketConfigEntry, MarketConfigs } from './types'

/**
 * Resolve the config a position's `mode` slot refers to — aware that the slot
 * is OVERLOADED, and that the two meanings must not collapse into one lookup.
 *
 * On most lenders `modes[posId]` is a CONFIG KEY (an Aave e-mode category, a
 * Euler controller id): `configs[mode]` either hits or the mode genuinely does
 * not exist for this market.
 *
 * On a PARAMETERIZED market — one whose base config declares an
 * `openParameter` — the slot carries the borrower's chosen VALUE instead
 * (LlamaLend's band count `N`). There is no `configs["17"]`, and there never
 * will be: the config advertises the DOMAIN, the position carries the value.
 * A plain `configs[mode]` lookup therefore misses, and every caller's
 * `?? 1` factor fallback then prices the collateral UNDISCOUNTED — health
 * reads `deposits/debt`, borrow capacity reads NAV-shaped, and a max-loop
 * quote overshoots what the Controller will accept. That is the bug this
 * resolver exists to end.
 *
 * Resolution:
 *  1. `configs[mode]` — the ordinary hit, always preferred.
 *  2. Else, if the base (`'0'`) config declares an `openParameter`: the mode is
 *     a parameter value. Return the base config, with the moved dimension
 *     re-priced from `openParameter.curve` when the curve carries this value —
 *     LlamaLend ships the full 4..50 LTV curve, so a real band count always
 *     resolves. A value the curve does not know (or a curve-less declaration,
 *     e.g. the borrowed-side row whose factors do not move with `N`) falls
 *     back to the base config's numbers — the DEFAULT point, which is at worst
 *     the old pre-`modes` behavior, never factor-1.
 *  3. Else `undefined` — a genuinely unknown mode on an unparameterized
 *     market, which callers already handle.
 */
export function resolveModeConfig(
  configs: MarketConfigs | null | undefined,
  mode: string,
): MarketConfigEntry | undefined {
  const direct = configs?.[mode]
  if (direct) return direct

  const base = configs?.['0']
  const op = base?.openParameter
  if (!base || !op) return undefined

  // IDENTITY parameters (Twyne): the chosen value IS the factor, so no curve is
  // needed. Falling through to the base config here would price every position
  // at the band's FLOOR — the whole point of the lender is that positions sit
  // above it.
  if (op.identityMapping && op.dimension === 'collateralFactor') {
    const value = Number(mode)
    if (Number.isFinite(value) && value > 0) {
      const inDomain =
        'values' in op.domain
          ? op.domain.values.includes(value)
          : value >= op.domain.min && value <= op.domain.max
      // Out-of-domain is not an error: both bounds move (governance ramps, and
      // Aave ramps PT eMode thresholds), so a position opened last week can
      // legitimately sit outside today's band. Its OWN threshold is still what
      // it is liquidated against.
      if (inDomain || value <= 1) {
        return { ...base, collateralFactor: value, borrowCollateralFactor: value }
      }
    }
  }

  const factor = op.curve?.[mode]
  if (factor == null || op.dimension !== 'collateralFactor') return base

  return {
    ...base,
    collateralFactor: factor,
    borrowCollateralFactor: factor,
  }
}
